// Reach Social marketing landing page.
//
// Public, no auth gate. The Get Started / sign-in CTAs invoke the
// shared OAuth flow via useAuth().signIn(); after auth, the user
// lands on /brand per the AuthProvider's post-auth handling.
//
// Adapted from reach_social_landing_page_mockup.jsx — theme tokens
// remapped to the app's existing reachSocialTheme (rsViolet/rsMagenta/
// rsOrange palettes + brand.* aliases). Stock Unsplash placeholders
// in the mockup are replaced with /landing/* paths served from
// frontend/app/public/landing/.

import {
  Box, Button, Container, Flex, Grid, GridItem, Heading, Text, Badge,
  HStack, VStack, SimpleGrid, Card, CardBody, Image, Icon, Avatar,
  AvatarGroup, Switch, Select
} from '@chakra-ui/react';
import {
  Sparkles, Play, CheckCircle2, ShoppingBag, Megaphone,
  Image as ImageIcon, ShieldCheck, BarChart3, Wand2, ArrowRight, Star,
  Heart, MessageCircle, LayoutGrid, Smartphone, Monitor, Package, Users
} from 'lucide-react';
import type { ComponentType, SVGProps, ReactNode } from 'react';
import { useAuth } from '../../auth/AuthContext';

// Reach Social signature gradient — matches the brand-button variant
// but in CSS-string form for inline use as backgrounds / text fills.
const RS_GRADIENT = 'linear-gradient(to right, #7A35E8, #D9008D, #F57C00)';

const gradientText = {
  bgGradient: 'linear(to-r, #F57C00, #D9008D, #7A35E8)',
  bgClip: 'text'
} as const;

// ── Brand mark ────────────────────────────────────────────────────

function Logo() {
  return (
    <HStack spacing={3}>
      <Box
        w="34px" h="34px"
        borderRadius="lg"
        bg={RS_GRADIENT}
        color="white"
        display="grid"
        placeItems="center"
        fontWeight="900"
        fontSize="18px"
      >
        R
      </Box>
      <Text fontWeight="800" letterSpacing="-0.02em" color="brand.ink">
        REACH SOCIAL
      </Text>
    </HStack>
  );
}

// ── Top navigation ────────────────────────────────────────────────

function TopNav({ onSignIn }: { onSignIn: () => void }) {
  return (
    <Box
      position="sticky" top={0} zIndex={20}
      bg="whiteAlpha.900" backdropFilter="blur(18px)"
      borderBottomWidth="1px" borderBottomColor="brand.border"
    >
      <Container maxW="1180px" py={4}>
        <Flex align="center" justify="space-between">
          <Logo />
          <HStack spacing={8} display={{ base: 'none', md: 'flex' }} fontSize="sm" color="brand.ink">
            <Text>Product</Text>
            <Text>Solutions</Text>
            <Text>Resources</Text>
            <Text>Pricing</Text>
            <Text>About</Text>
          </HStack>
          <HStack spacing={3}>
            <Button variant="ghost" size="sm" onClick={onSignIn}>Sign in</Button>
            <Button variant="brand" onClick={onSignIn}>Get Started Free</Button>
          </HStack>
        </Flex>
      </Container>
    </Box>
  );
}

// ── Mocked Generate-Ads preview panel (hero illustration) ──────────

type LucideIcon = ComponentType<SVGProps<SVGSVGElement>>;

function MiniIconButton({ icon, active }: { icon: LucideIcon; active?: boolean }) {
  return (
    <Box
      w="38px" h="38px"
      display="grid" placeItems="center"
      borderRadius="lg"
      bg={active ? 'rsViolet.50' : 'transparent'}
      color={active ? 'rsViolet.500' : 'brand.muted'}
    >
      <Icon as={icon} boxSize={4} />
    </Box>
  );
}

function AdPreviewPanel() {
  return (
    <Card borderRadius="3xl" overflow="hidden" bg="white" boxShadow="md">
      <CardBody p={0}>
        <Grid templateColumns="54px 1fr 170px" minH="446px">
          {/* App-shell rail */}
          <GridItem borderRightWidth="1px" borderRightColor="brand.border" pt={12}>
            <VStack spacing={4}>
              <MiniIconButton icon={LayoutGrid} />
              <MiniIconButton icon={ImageIcon} />
              <MiniIconButton icon={ShoppingBag} />
              <MiniIconButton icon={Megaphone} active />
              <MiniIconButton icon={BarChart3} />
              <MiniIconButton icon={ShieldCheck} />
            </VStack>
          </GridItem>

          {/* Ad preview canvas */}
          <GridItem p={7}>
            <Flex align="center" justify="space-between" mb={4}>
              <Text fontWeight="800" color="brand.ink">Ad Preview</Text>
              <Badge variant="processing">Live</Badge>
            </Flex>
            <HStack spacing={2} mb={5}>
              <Button size="xs" variant="outline" leftIcon={<Icon as={LayoutGrid} boxSize={3} />}>Square (1:1)</Button>
              <Button size="xs" variant="softBrand" leftIcon={<Icon as={Smartphone} boxSize={3} />}>Vertical (9:16)</Button>
              <Button size="xs" variant="outline" leftIcon={<Icon as={Monitor} boxSize={3} />}>Landscape</Button>
            </HStack>

            <Box borderRadius="2xl" overflow="hidden" borderWidth="1px" borderColor="brand.border" bg="gray.50">
              <Grid templateColumns="1fr 190px" minH="318px">
                <Box position="relative" overflow="hidden">
                  <Image
                    src="/landing/hero/creator.jpg"
                    alt="Creator content"
                    objectFit="cover" w="100%" h="100%"
                  />
                  <Box position="absolute" inset={0} bgGradient="linear(to-t, blackAlpha.500, transparent 55%)" />
                  <Box
                    position="absolute" left={4} bottom={4}
                    bg="white" borderRadius="2xl" p={3} maxW="210px" boxShadow="lg"
                  >
                    <HStack align="start" spacing={2}>
                      <Avatar size="sm" src="/landing/hero/creator-avatar.jpg" />
                      <Box>
                        <Text fontWeight="700" fontSize="xs" color="brand.ink">
                          Love this cap! Perfect for hikes.
                        </Text>
                        <Text fontSize="10px" color="brand.muted">@adventure_jen</Text>
                      </Box>
                    </HStack>
                    <HStack spacing={4} mt={2} fontSize="11px" color="brand.ink">
                      <HStack spacing={1}><Icon as={Heart} boxSize={3} color="rsOrange.400" /><Text>1.2k</Text></HStack>
                      <HStack spacing={1}><Icon as={MessageCircle} boxSize={3} /><Text>86</Text></HStack>
                    </HStack>
                  </Box>
                </Box>
                <Flex p={5} direction="column" justify="center" bg="linear-gradient(180deg,#FFFFFF,#F7F2EC)">
                  <Image
                    src="/landing/hero/product.jpg"
                    alt="Featured product"
                    h="126px"
                    objectFit="contain"
                    mb={5}
                  />
                  <Text fontWeight="800" color="brand.ink">Performance Cap</Text>
                  <Text color="brand.ink" fontSize="md">$29.00</Text>
                  <Button mt={5} variant="solid">Shop Now</Button>
                </Flex>
              </Grid>
            </Box>
          </GridItem>

          {/* Settings sidebar */}
          <GridItem borderLeftWidth="1px" borderLeftColor="brand.border" p={6} bg="white">
            <Text fontWeight="800" color="brand.ink" mb={5}>Ad Settings</Text>
            <Text fontSize="xs" color="brand.muted" mb={1}>Call to Action</Text>
            <Select size="sm" mb={5} defaultValue="Shop Now">
              <option>Shop Now</option>
            </Select>
            <VStack align="stretch" spacing={4} fontSize="sm" color="brand.ink">
              {['Show Comments', 'Show Likes', 'Show Price'].map((label) => (
                <Flex key={label} justify="space-between" align="center">
                  <Text>{label}</Text>
                  <Switch size="sm" colorScheme="purple" defaultChecked />
                </Flex>
              ))}
              <Flex justify="space-between" align="center">
                <Text>Show Brand Name</Text>
                <Switch size="sm" />
              </Flex>
            </VStack>
            <Text fontSize="xs" color="brand.muted" mt={6} mb={2}>Brand Colors</Text>
            <HStack spacing={2} mb={6}>
              {['#F57C00', '#7A35E8', '#0B1020', '#E5E7EB'].map((c) => (
                <Box key={c} w="18px" h="18px" borderRadius="full" bg={c} />
              ))}
            </HStack>
            <Button w="100%" size="sm" variant="softBrand" leftIcon={<Icon as={Sparkles} boxSize={4} />}>
              Generate More Ads
            </Button>
          </GridItem>
        </Grid>
      </CardBody>
    </Card>
  );
}

// ── Hero ──────────────────────────────────────────────────────────

function Hero({ onSignIn }: { onSignIn: () => void }) {
  return (
    <Box position="relative" overflow="hidden">
      {/* Soft blobs behind content */}
      <Box position="absolute" top="-180px" left="-160px" w="520px" h="520px" bg="rsViolet.100" filter="blur(80px)" opacity={0.65} />
      <Box position="absolute" top="90px" right="-140px" w="520px" h="520px" bg="rsOrange.100" filter="blur(90px)" opacity={0.75} />
      <Container maxW="1180px" pt={{ base: 12, md: 16 }} pb={8} position="relative">
        <Grid templateColumns={{ base: '1fr', lg: '0.86fr 1.14fr' }} gap={12} alignItems="center">
          <Box>
            <Badge variant="processing" px={3} py={2} mb={6}>
              <HStack spacing={2}>
                <Icon as={Play} boxSize={3.5} />
                <Text fontSize="xs">IMAGE → VIDEO · SOCIAL PROOF · EVERY SKU</Text>
              </HStack>
            </Badge>
            <Heading
              as="h1"
              fontSize={{ base: '44px', md: '60px' }}
              lineHeight="1.02"
              letterSpacing="-0.055em"
              color="brand.ink"
              maxW="620px"
            >
              A social-proof video ad for{' '}
              <Box as="span" {...gradientText}>every SKU in your catalog.</Box>
            </Heading>
            <Text mt={6} fontSize="lg" lineHeight="1.8" color="brand.muted" maxW="560px">
              Reach Social turns your existing catalog product images into on-brand Reels, TikToks, and
              PMax videos — with real customer reviews baked in as social proof. Reliable image-to-video
              at scale. Ships to Instagram, Google, and TikTok in minutes.
            </Text>
            <HStack spacing={4} mt={8}>
              <Button
                size="lg"
                variant="brand"
                rightIcon={<Icon as={ArrowRight} boxSize={5} />}
                px={7}
                h="58px"
                onClick={onSignIn}
              >
                Turn my catalog into videos
              </Button>
              <Button size="lg" variant="outline" h="58px" px={7} rightIcon={<Icon as={Play} boxSize={4} color="rsViolet.500" />}>
                Watch 90-second demo
              </Button>
            </HStack>
            <HStack spacing={8} mt={6} color="brand.muted" fontSize="sm" wrap="wrap">
              {['Product image → video, no shoot required', 'Every video quotes a real reviewer', 'Reels · TikTok · PMax from one seed'].map((item) => (
                <HStack key={item} spacing={2}>
                  <Icon as={CheckCircle2} boxSize={4} />
                  <Text>{item}</Text>
                </HStack>
              ))}
            </HStack>
            <HStack spacing={4} mt={9}>
              <AvatarGroup size="sm" max={4}>
                {[11, 12, 13, 14].map((i) => <Avatar key={i} src={`/landing/testimonials/avatar-${i}.jpg`} />)}
              </AvatarGroup>
              <Box>
                <HStack spacing={0.5}>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Icon key={i} as={Star} boxSize={4} fill="#FFB020" color="#FFB020" />
                  ))}
                </HStack>
                <Text fontSize="sm" color="brand.ink">
                  <b>4.8/5</b> from 2,000+ brands
                </Text>
              </Box>
            </HStack>
          </Box>
          <AdPreviewPanel />
        </Grid>
      </Container>
    </Box>
  );
}

// ── Problem section ──────────────────────────────────────────────
//
// Three sharp truths establishing the pain. Copy reads like a peer
// naming the reader's problem, not marketing pitching at them.

function Problem() {
  return (
    <Container maxW="900px" py={16} textAlign="center">
      <Text
        fontSize="xs" color="brand.muted"
        letterSpacing="0.14em" fontWeight="800"
        mb={4}
      >
        THE PROBLEM
      </Text>
      <Heading
        as="h2"
        fontSize={{ base: '32px', md: '44px' }}
        lineHeight="1.05"
        letterSpacing="-0.03em"
        color="brand.ink"
        mb={8}
      >
        Your catalog has 247 SKUs.<br />
        You have video ads for six.
      </Heading>
      <VStack spacing={4} color="brand.muted" fontSize="md" lineHeight="1.75" maxW="720px" mx="auto">
        <Text>
          Instagram Reels, TikTok, and Google PMax all reward video — and penalize you if you don't
          ship it. But you've got product photos, not a production studio, and your catalog grows every week.
        </Text>
        <Text>
          Your reviews are your strongest conversion asset. They're also stuck at the bottom of a
          product page, doing nothing for the 99% of people who never scroll that far.
        </Text>
        <Text>
          Video agencies quote $2,000+ per spot and take weeks. Generic AI video tools produce
          uncanny, off-brand results that don't reference your product, your reviews, or your look.
        </Text>
      </VStack>
    </Container>
  );
}

// ── Solution "Every X" three-column ──────────────────────────────
//
// Resolves three axes of the value prop (scale · proof · distribution)
// in one skim. The "Every X" rhythm is memorable + repeatable.

function Solution() {
  const columns: Array<[LucideIcon, string, string]> = [
    [ImageIcon,   'Image → video.',   'Your product photos become fully animated Reels and TikToks. No shoot, no models, no editor. Just a reliable image-to-video pipeline built for ecommerce catalogs.'],
    [Star,        'Social proof baked in.', 'We pull the highest-signal quotes and star ratings from your product page and layer them into every video — so the customer speaks, not the brand.'],
    [Smartphone,  'Every platform.',        'One catalog sync produces Reels (9:16), TikTok (9:16), PMax (16:9), Stories, and Feed (4:5) — on-brand, per SKU, ready to ship.'],
  ];
  return (
    <Container maxW="1180px" py={12}>
      <VStack spacing={3} mb={10} textAlign="center">
        <Text fontSize="xs" color="brand.muted" letterSpacing="0.14em" fontWeight="800">THE UNLOCK</Text>
        <Heading
          as="h2"
          fontSize={{ base: '30px', md: '40px' }}
          lineHeight="1.1"
          letterSpacing="-0.03em"
          color="brand.ink"
          maxW="820px"
        >
          Reliable image-to-video, infused with your customers' voices.
        </Heading>
      </VStack>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
        {columns.map(([I, title, desc]) => (
          <Card key={title} borderRadius="2xl" bg="white">
            <CardBody p={7}>
              <Box
                w="52px" h="52px"
                borderRadius="2xl"
                bg={RS_GRADIENT}
                color="white"
                display="grid" placeItems="center"
                mb={5}
                boxShadow="md"
              >
                <Icon as={I} boxSize={6} />
              </Box>
              <Heading as="h3" size="md" color="brand.ink" mb={2} letterSpacing="-0.02em">
                {title}
              </Heading>
              <Text fontSize="sm" color="brand.muted" lineHeight="1.7">{desc}</Text>
            </CardBody>
          </Card>
        ))}
      </SimpleGrid>
    </Container>
  );
}

// ── How it works ─────────────────────────────────────────────────

function StepCard({
  n, icon, title, desc, color, children
}: {
  n: string; icon: LucideIcon; title: string; desc: string;
  color: string; children?: ReactNode;
}) {
  return (
    <Card borderRadius="2xl" bg="white">
      <CardBody p={7}>
        <HStack spacing={4} align="start">
          <Box
            w="58px" h="58px"
            borderRadius="2xl"
            bg={color}
            color="white"
            display="grid" placeItems="center"
            boxShadow="md"
          >
            <Icon as={icon} boxSize={7} />
          </Box>
          <Box flex="1">
            <HStack spacing={3} mb={2}>
              <Badge bg="gray.100" color="brand.ink">{n}</Badge>
              <Text fontWeight="800" color="brand.ink">{title}</Text>
            </HStack>
            <Text fontSize="sm" color="brand.muted" lineHeight="1.7">{desc}</Text>
            {children}
          </Box>
        </HStack>
      </CardBody>
    </Card>
  );
}

function HowItWorks() {
  return (
    <Container maxW="1180px" py={12}>
      <Box borderRadius="3xl" borderWidth="1px" borderColor="brand.border" bg="whiteAlpha.800" p={{ base: 6, md: 8 }}>
        <VStack spacing={2} mb={8} textAlign="center">
          <Heading size="lg" color="brand.ink">How it works</Heading>
          <Text color="brand.muted">Catalog images in, social-proof videos out — reliably, at SKU scale.</Text>
        </VStack>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={5}>
          <StepCard n="1" icon={ShoppingBag} title="Connect your catalog" color="#7A35E8" desc="Shopify OAuth or paste your store URL. We ingest every product image, price, and review in minutes.">
            <HStack mt={5} spacing={3}>
              {[
                ['Shop', '#95BF47'],
                ['Meta', '#0B8BFF'],
                ['IG',   '#D9008D'],
                ['G',    '#4285F4']
              ].map(([label, color]) => (
                <Box
                  key={label}
                  w="30px" h="30px"
                  borderWidth="1px" borderColor="brand.border"
                  borderRadius="lg"
                  display="grid" placeItems="center"
                  color={color}
                  fontWeight="800"
                  fontSize="xs"
                >
                  {label}
                </Box>
              ))}
            </HStack>
          </StepCard>
          <StepCard n="2" icon={Package} title="Pick your SKUs" color="#D9008D" desc="One product, a whole category, or the entire catalog. Every selected SKU becomes a social-proof video campaign.">
            <HStack mt={5} spacing={2} color="brand.muted" fontSize="sm">
              <Badge variant="outline" fontSize="10px">1 SKU</Badge>
              <Badge variant="outline" fontSize="10px">Category</Badge>
              <Badge variant="outline" fontSize="10px">Full catalog</Badge>
            </HStack>
          </StepCard>
          <StepCard n="3" icon={Play} title="Videos generate" color="#F57C00" desc="Product images animate into Reels and TikToks. Real customer quotes and star ratings drop in as overlays. Every video is on-brand.">
            <HStack mt={5} spacing={1.5}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Icon key={i} as={Star} boxSize={3.5} fill="#FFB020" color="#FFB020" />
              ))}
              <Text ml={1} fontSize="xs" color="brand.muted" fontWeight="700">4.8 · 1.2k</Text>
            </HStack>
          </StepCard>
          <StepCard n="4" icon={Megaphone} title="Ship to IG, TikTok, Google" color="#2FB574" desc="One-click push to Meta Ads Manager, TikTok Ads, and Google PMax. Or export the MP4s and upload manually.">
            <HStack mt={5} spacing={3}>
              {[LayoutGrid, Smartphone, Monitor, Users].map((I, idx) => (
                <Box
                  key={idx}
                  w="30px" h="30px"
                  borderWidth="1px" borderColor="brand.border"
                  borderRadius="lg"
                  display="grid" placeItems="center"
                >
                  <Icon as={I} boxSize={3.5} />
                </Box>
              ))}
            </HStack>
          </StepCard>
        </SimpleGrid>
      </Box>
    </Container>
  );
}

// ── Ads gallery ───────────────────────────────────────────────────

const adExamples: Array<{
  label: string;
  title: string;
  price: string;
  img: string;
  quote: string;
  rating: string;
  likes: string;
  comments: string;
}> = [
  { label: 'IG Reel · 9:16 · Video', title: 'Trail Runner Pack', price: '$69.00',  img: '/landing/ads-gallery/ad-1.jpg', quote: '"Held up in a downpour."',        rating: '4.9 · 812 reviews',   likes: '1.4k', comments: '92' },
  { label: 'TikTok · 9:16 · Video',  title: 'Stormproof Jacket', price: '$149.00', img: '/landing/ads-gallery/ad-2.jpg', quote: '"Third one I\'ve bought."',       rating: '4.7 · 2.1k reviews',  likes: '2.3k', comments: '148' },
  { label: 'Google PMax · Video',    title: 'Hiker Boots',       price: '$129.00', img: '/landing/ads-gallery/ad-3.jpg', quote: '"Wore them in Iceland."',         rating: '4.8 · 1.2k reviews',  likes: '892',  comments: '42' },
  { label: 'IG Story · 9:16 · Video',title: 'Performance Cap',   price: '$29.00',  img: '/landing/ads-gallery/ad-4.jpg', quote: '"Perfect for long runs."',        rating: '4.9 · 480 reviews',   likes: '621',  comments: '28' }
];

type AdExample = typeof adExamples[number];

function MiniAdCard({ item, wide }: { item: AdExample; wide?: boolean }) {
  return (
    <Box borderWidth="1px" borderColor="brand.border" borderRadius="2xl" overflow="hidden" bg="white" boxShadow="sm">
      <Box position="relative" h={wide ? '180px' : '230px'}>
        <Image src={item.img} alt={item.title} w="100%" h="100%" objectFit="cover" />
        <Badge position="absolute" top={3} left={3} variant="processing">{item.label}</Badge>
        <Box position="absolute" left={3} bottom={3} bg="white" borderRadius="lg" p={2} boxShadow="md" maxW="160px">
          <Text fontSize="11px" fontWeight="700" color="brand.ink">{item.quote}</Text>
          <HStack mt={1} spacing={1}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Icon key={i} as={Star} boxSize={2.5} fill="#FFB020" color="#FFB020" />
            ))}
            <Text fontSize="9px" color="brand.muted" ml={1}>{item.rating}</Text>
          </HStack>
        </Box>
      </Box>
      <Flex align="center" justify="space-between" p={4}>
        <Box>
          <Text fontWeight="800" color="brand.ink" fontSize="sm">{item.title}</Text>
          <Text color="brand.ink" fontSize="13px">{item.price}</Text>
        </Box>
        <Button size="xs" variant="solid">Shop Now</Button>
      </Flex>
    </Box>
  );
}

function AdsGallery() {
  return (
    <Container maxW="1180px" py={2}>
      <Box borderWidth="1px" borderColor="brand.border" bg="white" borderRadius="3xl" p={8} boxShadow="sm">
        <Flex justify="space-between" align="center" mb={6} wrap="wrap" gap={3}>
          <Box>
            <Heading size="md" color="brand.ink">Catalog images in. Social-proof videos out.</Heading>
            <Text color="brand.muted" mt={1}>Every video quotes a verified reviewer. Reels · TikTok · PMax, ready for every SKU.</Text>
          </Box>
          <Button variant="outline" size="sm">See a full campaign</Button>
        </Flex>
        <Grid templateColumns={{ base: '1fr', md: '1fr 1fr 1.35fr 1fr' }} gap={6}>
          {adExamples.map((item, idx) => (
            <MiniAdCard key={item.title} item={item} wide={idx === 2} />
          ))}
        </Grid>
        <HStack justify="center" mt={6}>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Box key={i} w={i === 0 ? '8px' : '7px'} h="7px" borderRadius="full" bg={i === 0 ? 'rsViolet.500' : 'gray.300'} />
          ))}
        </HStack>
      </Box>
    </Container>
  );
}

// ── Testimonials ──────────────────────────────────────────────────

const testimonials: Array<[string, string, string]> = [
  ['We had zero video ads for 90% of our catalog. Now every SKU has a Reel, a TikTok, and a PMax video — same team, same budget. Our best video last quarter came from a review we didn\'t even know existed.', 'Sarah Thompson', 'Head of Growth, TrailBlaze'],
  ['Killed our $12k/mo video agency. First TikTok campaign built from Reach Social videos outperformed the agency spots by 38% on ROAS — and shipped in an afternoon.',                                          'James Park',     'CMO, Summit Supply Co.'],
  ['It just works. Every video renders, every quote sounds real, every SKU ships. That\'s the part no other AI video tool has cleared for us.',                                                                  'Maya Patel',     'Marketing Lead, Nimbus']
];

function Testimonials() {
  return (
    <Container maxW="1180px" py={10}>
      <Box borderRadius="3xl" bg="rsViolet.50" borderWidth="1px" borderColor="brand.border" p={8}>
        <Heading size="md" textAlign="center" color="brand.ink" mb={6}>
          Volume, economics, surprise — in the operators' own words
        </Heading>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
          {testimonials.map(([quote, name, role], idx) => (
            <Card key={name} borderRadius="2xl" bg="white">
              <CardBody p={6}>
                <Text color="brand.ink" lineHeight="1.7">&ldquo;{quote}&rdquo;</Text>
                <HStack mt={6} spacing={3}>
                  <Avatar size="sm" src={`/landing/testimonials/quote-${idx + 1}.jpg`} />
                  <Box>
                    <Text fontSize="13px" fontWeight="800">{name}</Text>
                    <Text fontSize="xs" color="brand.muted">{role}</Text>
                  </Box>
                </HStack>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
      </Box>
    </Container>
  );
}

// ── Integrations ──────────────────────────────────────────────────

const integrations: Array<[string, string, string]> = [
  ['Shopify',         'E-commerce',  '#95BF47'],
  ['Meta Ads',        'Advertising', '#0B8BFF'],
  ['Instagram',       'Social',      '#D9008D'],
  ['Google Merchant', 'Products',    '#4285F4'],
  ['TikTok Shop',     'E-commerce',  '#111111'],
  ['Facebook',        'Social',      '#1877F2']
];

function Integrations() {
  return (
    <Container maxW="1180px" py={4}>
      <Grid
        templateColumns={{ base: '1fr', lg: '230px 1fr' }}
        gap={6}
        alignItems="center"
        borderWidth="1px" borderColor="brand.border" borderRadius="3xl"
        p={7} bg="white" boxShadow="sm"
      >
        <Box>
          <Heading size="md" color="brand.ink">Works with the tools you already use</Heading>
          <Text color="brand.muted" fontSize="sm" mt={3}>
            Connect your favorite platforms and start creating in minutes.
          </Text>
        </Box>
        <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={4}>
          {integrations.map(([title, sub, color]) => (
            <Card key={title} borderRadius="2xl" boxShadow="none">
              <CardBody p={4} textAlign="center">
                <Box
                  mx="auto" mb={3}
                  w="36px" h="36px"
                  borderRadius="lg"
                  bg={`${color}14`}
                  color={color}
                  display="grid" placeItems="center"
                  fontWeight="900"
                >
                  {title[0]}
                </Box>
                <Text fontSize="13px" fontWeight="800" color="brand.ink">{title}</Text>
                <Text fontSize="11px" color="brand.muted">{sub}</Text>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
      </Grid>
    </Container>
  );
}

// ── Features ──────────────────────────────────────────────────────

const features: Array<[LucideIcon, string, string]> = [
  [Play,         'Reliable image-to-video',        'A hardened, tested video pipeline (not a wrapper around a flaky text-to-video toy). Your catalog images animate into on-brand Reels and TikToks — every time.'],
  [Wand2,        'Grounded in your brand',         'We detect your fonts, colors, and product photography, then lock every video to your visual system. No uncanny AI look.'],
  [Star,         'Every video quotes a real reviewer', 'A sentiment gate scores reviews for authenticity and specificity. Vague or negative quotes never make it into a video overlay.'],
  [Smartphone,   'Reels · TikTok · PMax from one seed', 'One SKU animates once, then re-composes for every placement. 9:16 Reels, 9:16 TikTok, 16:9 PMax, 4:5 Feed, Stories — all on-brand.'],
  [MessageCircle,'AI chat that edits, not restarts',   'Change the quote, the CTA, the pacing, the star count. In natural language. The video re-renders in seconds — you don\'t start over.'],
  [Megaphone,    'One-click push to IG, TikTok, Google', 'Ship an entire video campaign to Meta Ads Manager, TikTok Ads, and Google Ads PMax without leaving the tool. Or export MP4s.']
];

function Features() {
  return (
    <Container maxW="1180px" py={4}>
      <VStack spacing={2} mb={8} textAlign="center">
        <Text fontSize="xs" color="brand.muted" letterSpacing="0.14em" fontWeight="800">FEATURES</Text>
        <Heading size="lg" color="brand.ink" letterSpacing="-0.02em">Built for operators, not agencies</Heading>
      </VStack>
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={5}>
        {features.map(([I, title, desc]) => (
          <Card key={title} borderRadius="2xl">
            <CardBody p={6}>
              <Box
                w="48px" h="48px"
                borderRadius="2xl"
                bg="rsViolet.50"
                color="rsViolet.500"
                display="grid" placeItems="center"
                mb={4}
              >
                <Icon as={I} />
              </Box>
              <Text fontWeight="800" color="brand.ink" mb={2}>{title}</Text>
              <Text fontSize="sm" color="brand.muted" lineHeight="1.7">{desc}</Text>
            </CardBody>
          </Card>
        ))}
      </SimpleGrid>
    </Container>
  );
}

// ── Comparison table ──────────────────────────────────────────────
//
// Positions Reach Social against the three alternatives operators
// weigh — design agencies (slow + expensive), generic AI ad tools
// (fast + off-brand), DIY (cheap + doesn't scale). The middle column
// is the pitch: fast, on-brand, at catalog scale.

function ComparisonTable() {
  const rows: Array<[string, string, string, string, string]> = [
    ['Video output per SKU',        'Reel · TikTok · PMax · Story',   '1 spot per brief',              'Generic templates',                'Static only'],
    ['Time per video ad',           '2–5 minutes',                    '2–4 weeks',                      '30+ min per attempt (unreliable)', '4–8 hours'],
    ['Cost per video ad',           '~$5',                            '$2,000–$8,000',                  '$50–$200/mo + retries',            'Your time'],
    ['Reliable at catalog scale',   'Yes — hundreds of SKUs/hour',    'No',                             'No — most jobs fail or drift',     'No'],
    ['On-brand fonts + colors',     'Auto-detected from your site',   'Yes (you brief them)',           'Rarely',                            'Yes (manual)'],
    ['Every video quotes a reviewer', 'Yes — sentiment-gated',        'If briefed',                     'No',                                'Copy/paste manually'],
    ['Push to IG, TikTok, Google',  'One click',                      'Manual handoff',                 'Export only',                       'Manual']
  ];
  return (
    <Container maxW="1180px" py={12}>
      <VStack spacing={3} mb={8} textAlign="center">
        <Text fontSize="xs" color="brand.muted" letterSpacing="0.14em" fontWeight="800">HOW WE COMPARE</Text>
        <Heading
          as="h2"
          fontSize={{ base: '28px', md: '36px' }}
          lineHeight="1.1"
          letterSpacing="-0.03em"
          color="brand.ink"
          maxW="760px"
        >
          Reliable social-proof video at catalog scale — nothing else clears the bar.
        </Heading>
      </VStack>
      <Box
        borderWidth="1px"
        borderColor="brand.border"
        borderRadius="3xl"
        overflow="hidden"
        bg="white"
        boxShadow="sm"
      >
        <Grid
          templateColumns={{ base: '1fr', md: '1.3fr 1.1fr 1fr 1fr 1fr' }}
          bg="gray.50"
          borderBottomWidth="1px"
          borderBottomColor="brand.border"
        >
          <Box p={4} />
          <Box p={4} bg="white" borderLeftWidth="1px" borderRightWidth="1px" borderColor="brand.border">
            <Text fontWeight="900" color="brand.ink" fontSize="sm" {...gradientText}>Reach Social</Text>
            <Text fontSize="xs" color="brand.muted" mt={0.5}>Us</Text>
          </Box>
          <Box p={4}>
            <Text fontWeight="800" color="brand.ink" fontSize="sm">Video agency</Text>
            <Text fontSize="xs" color="brand.muted" mt={0.5}>Slow + expensive</Text>
          </Box>
          <Box p={4} borderLeftWidth="1px" borderColor="brand.border">
            <Text fontWeight="800" color="brand.ink" fontSize="sm">Generic AI video tool</Text>
            <Text fontSize="xs" color="brand.muted" mt={0.5}>Flaky + off-brand</Text>
          </Box>
          <Box p={4} borderLeftWidth="1px" borderColor="brand.border">
            <Text fontWeight="800" color="brand.ink" fontSize="sm">DIY (Canva)</Text>
            <Text fontSize="xs" color="brand.muted" mt={0.5}>Static + doesn't scale</Text>
          </Box>
        </Grid>
        {rows.map(([label, us, agency, ai, diy], idx) => (
          <Grid
            key={label}
            templateColumns={{ base: '1fr', md: '1.3fr 1.1fr 1fr 1fr 1fr' }}
            borderTopWidth={idx === 0 ? 0 : '1px'}
            borderColor="brand.border"
          >
            <Box p={4} bg="gray.50">
              <Text fontSize="sm" fontWeight="700" color="brand.ink">{label}</Text>
            </Box>
            <Box p={4} bg="white" borderLeftWidth="1px" borderRightWidth="1px" borderColor="brand.border">
              <Text fontSize="sm" color="brand.ink" fontWeight="700">{us}</Text>
            </Box>
            <Box p={4}>
              <Text fontSize="sm" color="brand.muted">{agency}</Text>
            </Box>
            <Box p={4} borderLeftWidth="1px" borderColor="brand.border">
              <Text fontSize="sm" color="brand.muted">{ai}</Text>
            </Box>
            <Box p={4} borderLeftWidth="1px" borderColor="brand.border">
              <Text fontSize="sm" color="brand.muted">{diy}</Text>
            </Box>
          </Grid>
        ))}
      </Box>
    </Container>
  );
}

// ── Final CTA ─────────────────────────────────────────────────────

function FinalCTA({ onSignIn }: { onSignIn: () => void }) {
  return (
    <Container maxW="1180px" pt={8} pb={12}>
      <Box position="relative" overflow="hidden" borderRadius="3xl" p={{ base: 7, md: 10 }} bg="brand.ink" color="white" boxShadow="brand">
        <Box
          position="absolute" right="-80px" top="-80px"
          w="360px" h="360px"
          bg={RS_GRADIENT}
          filter="blur(28px)" opacity={0.65}
          borderRadius="full"
        />
        <Flex
          position="relative"
          align={{ base: 'start', md: 'center' }}
          justify="space-between"
          direction={{ base: 'column', md: 'row' }}
          gap={6}
        >
          <Box maxW="640px">
            <Heading size="lg" lineHeight="1.1" letterSpacing="-0.02em">
              Your next video ad already exists.<br />
              <Box as="span" bgGradient="linear(to-r, #F57C00, #D9008D)" bgClip="text">
                It's a product photo + a five-star review.
              </Box>
            </Heading>
            <Text color="whiteAlpha.800" mt={3}>
              Connect your store, pick a SKU, and watch a social-proof video build itself in under two minutes.
              Reels, TikTok, and PMax ready. Free to try — no credit card, no shoot, no editor.
            </Text>
          </Box>
          <HStack spacing={4} wrap="wrap">
            <Button variant="brand" rightIcon={<Icon as={ArrowRight} />} h="52px" px={7} onClick={onSignIn}>
              Turn my catalog into videos
            </Button>
            <Button variant="outline" color="white" borderColor="whiteAlpha.500" h="52px" px={7} _hover={{ bg: 'whiteAlpha.200' }}>
              Watch 90-second demo
            </Button>
          </HStack>
        </Flex>
      </Box>
    </Container>
  );
}

// ── Page composition ─────────────────────────────────────────────

export function LandingPage() {
  const auth = useAuth();
  return (
    <Box bg="linear-gradient(180deg,#FFFFFF 0%, #F8FAFC 45%, #FFFFFF 100%)" minH="100vh">
      <TopNav onSignIn={auth.signIn} />
      <Hero onSignIn={auth.signIn} />
      <Problem />
      <Solution />
      <HowItWorks />
      <AdsGallery />
      <Features />
      <ComparisonTable />
      <Testimonials />
      <Integrations />
      <FinalCTA onSignIn={auth.signIn} />
    </Box>
  );
}
